import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import NWLContext from '../../../contexts/NWLContext';
import * as antIcons from '@ant-design/icons';
import NoDataCloud from '../../../assets/svg/NoDataCloud';
import Dropdown from '../Common/Dropdown';
import './kpi.scss';
import {
	getDigitalTwins,
	getDigitalTwinsSimulations,
	getDigitalTwinsNextSimulation,
	getKpis,
} from '../../../services/digitalTwins';
import { CountdownInfoModal } from '@/components/Modal/CountdownInfo';
import { capitaliseWord, roundTo } from '../../../utils';
import { lockFirstSuccessCompletedSimTime } from '../../utils/simSuccessTime';
import { getImpactedForecast, getImpactedForecastTimeline } from '../../../services/forecastImpact';
import { useActiveLocationId } from '../../utils/activeLocation';
import { nwlKeys } from '../../utils/Utils';
import { createPortal } from 'react-dom';
import { getCurrentStrategies } from '../../../services/strategies';

const { WarningOutlined, ToolOutlined } = antIcons;

const mapStatusText = (s) =>
	s === 'IN_PROGRESS'
		? 'Data update in progress...'
		: s === 'COMPLETED'
			? 'Data update was successful'
			: s === 'IDLE'
				? 'Loading latest data...'
				: 'Last data update failed';

function currentStatus(payload) {
	if (!payload) return 'IDLE';
	const s = payload?.data?.[0]?.summary?.status;
	return s === 'COMPLETED' || s === 'IN_PROGRESS' || s === 'FAILED' ? s : 'FAILED';
}

function latestCompletedSimulation(payload) {
	const arr = Array.isArray(payload?.data) ? payload.data : [];
	const now = arr[0];
	if (now?.summary?.status === 'COMPLETED') return now;
	return arr.slice(1).find((b) => b?.summary?.status === 'COMPLETED') || null;
}

function kpiArrayToObject(kpisArr = [], kpisPayload = null) {
	const kpiObject = {};
	for (const k of kpisArr) {
		if (!k) continue;
		if (k.type === 'disch_vol') {
			kpiObject.dischVol = k.value;
			kpiObject.dischVolUnit = k.unit;
		} else if (k.type === 'flood_num' || k.type === 'disch_loc') {
			continue;
		} else if (k.type === 'disch_num') {
			kpiObject.dischNum = k.value;
		} else {
			kpiObject[k.type] = k.value;
		}
	}

	const floodNodes = Array.isArray(kpisPayload?.floods?.nodes)
		? kpisPayload.floods.nodes
		: [];

	const dischargeNodes = Array.isArray(kpisPayload?.discharges?.nodes)
		? kpisPayload.discharges.nodes
		: [];

	kpiObject.floodNum = floodNodes.length;
	kpiObject.dischLoc = dischargeNodes.length;

	return kpiObject;
}

function kpiExtraction(bucket, strategyValue, kpisPayload) {
	if (!bucket) return null;
	const sims = Array.isArray(bucket.simulations) ? bucket.simulations : [];
	let sim = sims.find(
		(s) => s?.strategy === strategyValue && Array.isArray(s?.kpis)
	);
	if (!sim) {
		sim = sims.find((s) => Array.isArray(s?.kpis));
	}
	if (!sim) return null;
	return kpiArrayToObject(sim.kpis, kpisPayload);
}

function toDisplayPairs(kpis) {
	if (!kpis) return [];

	const num = (v) => (v == null ? null : Number(v));
	const safeCeil = (v) => {
		const n = num(v);
		return Number.isFinite(n) ? Math.ceil(n) : null;
	};
	const safeRound = (v, display = 1) => {
		const n = num(v);
		return Number.isFinite(n) ? roundTo(n, display) : null;
	};

	const flood = safeCeil(kpis.floodNum);
	const dischLoc = safeCeil(kpis.dischLoc);
	const dischVol = safeRound(kpis.dischVol, 1);

	return [
		{ testId: 'flood-locations', value: flood, description: 'Flood locations' },
		{ testId: 'discharge-locations', value: dischLoc, description: 'Discharge locations' },
		{
			testId: 'discharged',
			value: dischVol == null ? null : dischVol,
			description: `Discharged (${kpis.dischVolUnit || 'ML'})`,
		},
	].filter((x) => x.value != null);
}

function useNextSimTime(key = 'nwl:nextSimulationEpoch') {
	const [secs, setSecs] = React.useState(null);

	React.useEffect(() => {
		const compute = () => {
			const epoch = Number(localStorage.getItem(key) || '0');
			if (Number.isFinite(epoch) && epoch > 0) {
				const now = Math.floor(Date.now() / 1000);
				setSecs(Math.max(0, epoch - now));
			} else {
				setSecs(null);
			}
		};

		compute();
		const id = setInterval(compute, 1000);

		const onStorage = (e) => {
			if (e.key === key) compute();
		};
		window.addEventListener('storage', onStorage);

		const onUpdated = () => compute();
		window.addEventListener('nwl:simulation-updated', onUpdated);

		return () => {
			clearInterval(id);
			window.removeEventListener('storage', onStorage);
			window.removeEventListener('nwl:simulation-updated', onUpdated);
		};
	}, [key]);

	return React.useMemo(() => {
		if (secs == null) return null;
		const mm = String(Math.floor(secs / 60)).padStart(2, '0');
		const ss = String(secs % 60).padStart(2, '0');
		return `${mm}:${ss} mins.`;
	}, [secs]);
}

const fetchImpactedForecast = async (orgId, locId, dtId, simId) => {
	const response = await getImpactedForecast(orgId, locId, dtId, simId);
	try {
		const data = Array.isArray(response) ? response : response?.data;
		window.dispatchEvent(new CustomEvent('nwl:impacted-forecast', { detail: data ?? [] }));
	} catch {}
	return response;
};

const fetchImpactedForecastTimeline = async (orgId, locId, dtId, simId) => {
	const response = await getImpactedForecastTimeline(orgId, locId, dtId, simId);
	try {
		const data = Array.isArray(response) ? response : response?.data;
		window.dispatchEvent(new CustomEvent('nwl:impacted-forecast-timeline', { detail: data ?? [] }));
	} catch {}
	return response;
};

const toSafeType = (type) =>
	String(type || '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_');

const ICON_NUDGE = {
	conduit: { x: 4, y: -1, size: 34 },
};

const LocationTypeIcon = ({ type }) => {
	const safeType = toSafeType(type);
	const iconSrc = `/img/nwlIcons/new/${safeType}.svg`;
	const nudge = ICON_NUDGE[safeType] ?? { x: 0, y: 0 };
	const glyphSize = nudge.size ?? 34;

	return (
		<div className="kpi-locations-icon">
			<div
				className="kpi-locations-icon-mask"
				id={`kpi-locations-icon-${safeType}`}
				style={{
					width: glyphSize,
					height: glyphSize,
					WebkitMaskImage: `url(${iconSrc})`,
					maskImage: `url(${iconSrc})`,
					transform: `translate(${nudge.x}px, ${nudge.y}px)`,
				}}
			/>
		</div>
	);
};

const buildLocationsFromKpis = (kpisPayload, kind) => {
	if (!kpisPayload) return [];

	const isDischarge = kind === 'discharge';
	const bucket = isDischarge ? kpisPayload?.discharges : kpisPayload?.floods;
	const nodes = Array.isArray(bucket?.nodes) ? bucket.nodes : [];

	const pickValue = (summary = {}) => {
		if (summary?.total_vol != null) return Number(summary.total_vol);
		return null;
	};

	return nodes
		.map((n, idx) => {
			const name = n?.node?.name || `Item ${idx + 1}`;
			const value = pickValue(n?.summary);
			if (!Number.isFinite(value)) return null;

			const unit = isDischarge ? 'ML' : 'ML';

			return {
				id: n?.node?.name ?? `${kind}-${idx}`,
				name,
				value,
				unit,
				type: n?.node?.type,
				coordinates: n?.node?.coordinates?.[0],
			};
		})
		.filter(Boolean)
		.sort((a, b) => b.value - a.value);
};

const moveToCoord = (coord) => {
	if (!coord) return null;

	if (Array.isArray(coord) && coord.length >= 2) {
		const lon = Number(coord[0]);
		const lat = Number(coord[1]);
		return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
	}

	const lon = Number(coord.longitude);
	const lat = Number(coord.latitude);
	return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
};

const focusMapTo = (coord, zoom = 18, imgType, name) => {
	const lonLat = moveToCoord(coord);
	if (!lonLat) return;

	window.dispatchEvent(
		new CustomEvent('nwl:map-focus', {
			detail: { longitude: lonLat[0], latitude: lonLat[1], zoom, imgType, name },
		}),
	);
};

const LocationsModal = ({ open, title, items, onClose }) => {
	React.useEffect(() => {
		if (!open) return;
		const onKey = (e) => e.key === 'Escape' && onClose();
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	if (!open) return null;

	return createPortal(
		<div className="kpi-locations-screen">
			<div className="kpi-locations-panel">
				<div className="kpi-locations-header">
					<div id="kpi-locations-back-button" data-testid="locations-back-button">
						<svg
							onClick={onClose}
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							style={{ cursor: 'pointer' }}
						>
							<path
								d="M14.1667 9.16666H7.84166L10.5917 6.42499C10.7486 6.26807 10.8367 6.05524 10.8367 5.83333C10.8367 5.61141 10.7486 5.39858 10.5917 5.24166C10.4347 5.08474 10.2219 4.99658 10 4.99658C9.77808 4.99658 9.56525 5.08474 9.40833 5.24166L5.24166 9.40833C5.16579 9.48758 5.10632 9.58103 5.06666 9.68333C4.98331 9.88621 4.98331 10.1138 5.06666 10.3167C5.10632 10.419 5.16579 10.5124 5.24166 10.5917L9.40833 14.7583C9.4858 14.8364 9.57796 14.8984 9.67951 14.9407C9.78106 14.983 9.88999 15.0048 10 15.0048C10.11 15.0048 10.2189 14.983 10.3205 14.9407C10.422 14.8984 10.5142 14.8364 10.5917 14.7583C10.6698 14.6809 10.7318 14.5887 10.7741 14.4871C10.8164 14.3856 10.8382 14.2767 10.8382 14.1667C10.8382 14.0566 10.8164 13.9477 10.7741 13.8462C10.7318 13.7446 10.6698 13.6525 10.5917 13.575L7.84166 10.8333H14.1667C14.3877 10.8333 14.5996 10.7455 14.7559 10.5892C14.9122 10.433 15 10.221 15 9.99999C15 9.77898 14.9122 9.56702 14.7559 9.41074C14.5996 9.25446 14.3877 9.16666 14.1667 9.16666Z"
								fill="#023E65"
							/>
						</svg>
					</div>
					<div
						className="kpi-locations-title"
						id={`locations-title-${title?.toLowerCase() || 'default'}`}
					>
						{title}
					</div>
				</div>

				<div className="kpi-locations-body">
					{items?.length ? (
						items.map((x) => {
							const hasCoord = !!moveToCoord(x.coordinates);
							return (
								<div
									key={x.id}
									className="kpi-locations-row"
									data-testid={`location-row-${x.id}`}
									id={`kpi-location-row-${x.id}`}
									role={hasCoord ? 'button' : undefined}
									tabIndex={hasCoord ? 0 : undefined}
									style={hasCoord ? { cursor: 'pointer' } : undefined}
									onClick={() => {
										if (!hasCoord) return;
										focusMapTo(x.coordinates, 18, x.type, x.name);
										onClose();
									}}
									onKeyDown={(e) => {
										if (!hasCoord) return;
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											focusMapTo(x.coordinates, 18, x.type, x.name);
											onClose();
										}
									}}
								>
									<div className="kpi-locations-left">
										<LocationTypeIcon type={x.type} />

										<div
											className="kpi-locations-name"
											data-testid={`location-name-${x.id}`}
											id={`kpi-location-name-${x.id}`}
										>
											{x.name}
										</div>
									</div>

									<div
										className="kpi-locations-value"
										data-testid={`location-value-${x.id}`}
										id={`kpi-location-value-${x.id}`}
									>
										{roundTo(x.value, 3)}
										{x.unit}
									</div>
								</div>
							);
						})
					) : (
						<div className="kpi-locations-empty" id="location-no-data">
							No data for {title?.toLowerCase()} available
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};

/* ---------- MAIN ---------- */

const Kpi = ({ kpiIndex, strategyOnChange = () => {}, displayColumn = false }) => {
	const { decisionEngine } = useContext(NWLContext);

	const locationId = useActiveLocationId();
	const [digitalTwinId, setDigitalTwinId] = useState(null);
	const [livePayload, setLivePayload] = useState(null); // fresh, in-memory simulations
	const [tick, setTick] = useState(0);
	const [showCountdownInfo, setShowCountdownInfo] = useState(false);
	const [targetEpochSec, setTargetEpochSec] = useState(null);

	const userPickedStrategyRef = useRef(false);
	const fetchingRef = useRef(false);
	const [initialising, setInitialising] = useState(true);
	const lastRecommendedSimIdRef = useRef(null);

	const [organizationId, setOrganizationId] = useState(null);
	const [showFloodInfo, setShowFloodInfo] = useState(false);
	const [floodIconActive, setFloodIconActive] = useState(false);
	const timelineIsDefaultRef = useRef(true);
	const [openLocations, setOpenLocations] = useState(null);
	const [kpisPayload, setKpisPayload] = useState(null);
	const [implementedStrategy, setImplementedStrategy] = useState(null);

	const fetchKpisForSim = useCallback(async (orgId, locId, dtId, simId) => {
		if (!orgId || !locId || !dtId || !simId) return;
		try {
			const resp = await getKpis(orgId, locId, dtId, simId);

			const data = resp?.data ?? resp;
			setKpisPayload(data || null);
		} catch (e) {
			console.warn('getKpis failed', e);
			setKpisPayload(null);
		}
	}, []);

	const [lastSuccessfulRefreshTime, setLastSuccessfulRefreshTime] = useState(() => {
		const iso = localStorage.getItem('nwl:lastSuccessAtISO');
		if (!iso) return undefined;
		const d = new Date(iso);
		return Number.isNaN(d.getTime()) ? undefined : d;
	});

	const extractPayloadFromSimResp = useCallback((simResp) => {
		if (!simResp) return null;
		if (Array.isArray(simResp)) return simResp.find((p) => Array.isArray(p?.data)) ?? null;
		if (Array.isArray(simResp?.data)) return simResp;
		return null;
	}, []);

	// ---- cross-instance guard so both KPI cards don't double-fetch ----
	const getGlobal = () => window || {};
	const isGloballyFetching = () => Boolean(getGlobal().__nwl_kpi_fetching);
	const setGloballyFetching = (val) => {
		getGlobal().__nwl_kpi_fetching = Boolean(val);
	};

	const setGlobalPayload = (payload) => {
		getGlobal().__nwl_kpi_payload = payload;
	};

	// Timeline default change listener
	useEffect(() => {
		const onDefaultChanged = (e) => {
			timelineIsDefaultRef.current = Boolean(e?.detail?.isDefault);
		};

		window.addEventListener('nwl:timeline-default-changed', onDefaultChanged);
		return () => window.removeEventListener('nwl:timeline-default-changed', onDefaultChanged);
	}, []);

	// Centralized fetch (used on mount, every 60s, and when next-sim time is reached)
	const fetchAll = useCallback(async () => {
		if (!locationId) return;
		if (fetchingRef.current || isGloballyFetching()) return;
		fetchingRef.current = true;
		setGloballyFetching(true);

		try {
			const security = localStorage.getItem('security');
			const organizationId = JSON.parse(security ?? '{}')?.organizationId;
			setOrganizationId(organizationId);

			if (!organizationId || !locationId) throw new Error('No organization/location');

			// fetch implemented/current strategy from network
			try {
				const current = await getCurrentStrategies(locationId, organizationId);
				const implemented = current?.mode;
				setImplementedStrategy(implemented);
			} catch (e) {
				console.warn('getCurrentStrategies failed', e);
				setImplementedStrategy(null);
			}

			// get a digitalTwinId (reuse if we already have one)
			let dtId = digitalTwinId;
			if (!dtId) {
				const resp = await getDigitalTwins(organizationId, locationId);

				if (resp) {
					dtId = resp?.id ?? null;
					if (dtId) setDigitalTwinId(dtId);
				}
			}
			if (!dtId) throw new Error('No digital twin id');

			// fetch "next simulation windows" and update countdown
			const nextResults = await getDigitalTwinsNextSimulation(organizationId, locationId, dtId);
			const epochs = (Array.isArray(nextResults) ? nextResults : [nextResults])
				.map((r) => Number(r?.nextSimulation))
				.filter((n) => Number.isFinite(n) && n > 0);
			if (epochs.length) {
				const soonest = Math.min(...epochs);
				setTargetEpochSec(soonest);
				localStorage.setItem('nwl:nextSimulationEpoch', String(soonest));
			} else {
				setTargetEpochSec(null);
				localStorage.removeItem('nwl:nextSimulationEpoch');
			}

			// fetch simulations (live only)
			const simResults = await getDigitalTwinsSimulations(organizationId, locationId, dtId);

			const payload = extractPayloadFromSimResp(simResults);

			setLivePayload(payload);
			setGlobalPayload(payload);

			latestCompletedSimulation(payload);
			lockFirstSuccessCompletedSimTime(payload);

			// notify listeners – include payload so the sibling KPI instance can adopt it
			window.dispatchEvent(new CustomEvent('nwl:simulation-updated', { detail: { payload } }));
		} catch {
			console.warn('Failed to fetch KPI data');
			window.dispatchEvent(new CustomEvent('nwl:impacted-forecast', { detail: [] }));
			window.dispatchEvent(new CustomEvent('nwl:impacted-forecast-timeline', { detail: [] }));
		} finally {
			setInitialising(false);
			fetchingRef.current = false;
			setGloballyFetching(false);
			setTick((x) => x + 1);
		}
	}, [locationId, digitalTwinId, extractPayloadFromSimResp]);

	useEffect(() => {
		const onManualRefresh = () => {
			// allow a one-off update even if not default
			fetchAll();
		};

		window.addEventListener('nwl:timeline-manual-refresh', onManualRefresh);
		return () => window.removeEventListener('nwl:timeline-manual-refresh', onManualRefresh);
	}, [fetchAll]);

	const findSimulationIdForStrategy = useCallback((payload, strategy) => {
		if (!strategy) return { simulation: null, bucket: null };

		const buckets = Array.isArray(payload?.data) ? payload.data : [];
		const current = buckets[0];
		const previous = buckets[1];

		const pickFromBucket = (bucket) => {
			if (!bucket || bucket?.summary?.status !== 'COMPLETED') return null;
			const sim = (bucket?.simulations ?? []).find((s) => s?.strategy === strategy);
			return sim ? { simulation: sim, bucket } : null;
		};

		return (
			pickFromBucket(current) || pickFromBucket(previous) || { simulation: null, bucket: null }
		);
	}, []);

	// Initial fetch on mount (always)
	useEffect(() => {
		if (!locationId) return;
		fetchAll();
	}, [locationId, fetchAll]);

	// Re-fetch when next-simulation time is reached (checks every second)
	useEffect(() => {
		if (targetEpochSec == null) return;
		const tick = setInterval(() => {
			const now = Math.floor(Date.now() / 1000);
			if (now >= targetEpochSec) {
				if (!timelineIsDefaultRef.current) return;
				fetchAll(); // schedules next epoch inside
			}
		}, 1000);
		return () => clearInterval(tick);
	}, [targetEpochSec, fetchAll]);

	// Background refresh every 60s (so cards stay fresh even if epoch is far away)
	useEffect(() => {
		const id = setInterval(() => {
			if (!timelineIsDefaultRef.current) return;
			fetchAll();
		}, 60000);
		return () => clearInterval(id);
	}, [fetchAll]);

	useEffect(() => {
		const onUpdated = (e) => {
			setInitialising(false);
			if (e?.detail?.payload) setLivePayload(e.detail.payload);
			const iso = localStorage.getItem('nwl:lastSuccessAtISO');
			setLastSuccessfulRefreshTime(iso ? new Date(iso) : undefined);
			setTick((x) => x + 1);
		};
		window.addEventListener('nwl:simulation-updated', onUpdated);
		return () => window.removeEventListener('nwl:simulation-updated', onUpdated);
	}, []);

	const floodLocations = useMemo(() => buildLocationsFromKpis(kpisPayload, 'flood'), [kpisPayload]);

	const dischargeLocations = useMemo(
		() => buildLocationsFromKpis(kpisPayload, 'discharge'),
		[kpisPayload],
	);

	const activePayload = livePayload;

	const options = useMemo(() => {
		const baseOptions = [];
		const dropdown = new Set();
		const allStrategies = Array.isArray(activePayload?.data) ? activePayload.data : [];
		allStrategies.forEach((b) =>
			(b?.simulations ?? []).forEach((s) => {
				const v = typeof s?.strategy === 'string' ? s.strategy.trim() : '';
				if (v && !dropdown.has(v)) {
					baseOptions.push({ value: v, displayText: capitaliseWord(v) });
					dropdown.add(v);
				}
			}),
		);
		baseOptions.sort((a, b) =>
			(a.displayText ?? `${a.value}`).localeCompare(b.displayText ?? `${b.value}`),
		);
		return baseOptions;
	}, [activePayload]);

	// selected index
	const [selectedIndex, setSelectedIndex] = useState(() => {
		const def =
			kpiIndex === 1
				? decisionEngine?.strategyIndexes?.recommended
				: decisionEngine?.strategyIndexes?.default;
		return Number.isFinite(def) ? Number(def) : 0;
	});

	// auto-select recommended (until user picks)
	useEffect(() => {
		if (userPickedStrategyRef.current) return;
		const completed = latestCompletedSimulation(activePayload);
		const recommended = completed?.summary?.recommendedStrategy;
		if (!recommended) return;
		const idx = options.findIndex((o) => o.value === recommended);
		if (idx !== -1 && idx !== selectedIndex) {
			setSelectedIndex(idx);
			strategyOnChange(options[idx].value);
		}
	}, [
		options,
		selectedIndex,
		strategyOnChange,
		activePayload,
		organizationId,
		locationId,
		digitalTwinId,
	]);

	const handleSelect = (idxOrValue) => {
		userPickedStrategyRef.current = true;
		let idx =
			typeof idxOrValue === 'number'
				? idxOrValue
				: options.findIndex((o) => String(o.value) === String(idxOrValue));

		if (idx < 0) idx = 0;

		const opt = options[idx];
		setSelectedIndex(idx);
		strategyOnChange(opt?.value);

		const { simulation } = findSimulationIdForStrategy(activePayload, opt?.value);

		if (simulation && organizationId && locationId && digitalTwinId) {
			const simId = simulation.simulationId;
			localStorage.setItem(nwlKeys.dtId(organizationId, locationId), String(digitalTwinId));
			localStorage.setItem(nwlKeys.simId(organizationId, locationId), String(simId));
			fetchImpactedForecast(organizationId, locationId, digitalTwinId, simId);
			fetchImpactedForecastTimeline(organizationId, locationId, digitalTwinId, simId);
			fetchKpisForSim(organizationId, locationId, digitalTwinId, simId);
		}
	};

	useEffect(() => {
		if (!activePayload) return;

		const completed = latestCompletedSimulation(activePayload);
		const recommended = completed?.summary?.recommendedStrategy;
		if (!recommended) return;

		const idx = options.findIndex((o) => o.value === recommended);

		if (!userPickedStrategyRef.current && idx !== -1 && idx !== selectedIndex) {
			setSelectedIndex(idx);
			strategyOnChange(options[idx].value);
		}

		const { simulation } = findSimulationIdForStrategy(activePayload, recommended);
		if (!simulation || !organizationId || !locationId || !digitalTwinId) return;

		const simId = simulation.simulationId;
		if (!simId) return;

		if (lastRecommendedSimIdRef.current === simId) return;
		lastRecommendedSimIdRef.current = simId;

		localStorage.setItem(nwlKeys.dtId(organizationId, locationId), String(digitalTwinId));
		localStorage.setItem(nwlKeys.simId(organizationId, locationId), String(simId));

		fetchImpactedForecast(organizationId, locationId, digitalTwinId, simId);
		fetchImpactedForecastTimeline(organizationId, locationId, digitalTwinId, simId);
		fetchKpisForSim(organizationId, locationId, digitalTwinId, simId);
	}, [
		activePayload,
		options,
		selectedIndex,
		strategyOnChange,
		organizationId,
		locationId,
		digitalTwinId,
		findSimulationIdForStrategy,
	]);

	// status/banner
	const payloadStatus = useMemo(() => currentStatus(activePayload), [activePayload]);
	const status = payloadStatus;
	const [bannerVisible, setBannerVisible] = useState(true);

	const displayFailedCard = useMemo(() => {
		if (initialising) return false; // during initial load, never show failed card
		const arr = Array.isArray(activePayload?.data) ? activePayload.data : [];
		const b0 = arr[0];
		const prevCompleted = arr.slice(1).find((b) => b?.summary?.status === 'COMPLETED');
		const hasCompleted = b0?.summary?.status === 'COMPLETED' || !!prevCompleted;
		return !hasCompleted;
	}, [status, activePayload]);

	const remainingRefreshTime = useNextSimTime();

	useEffect(() => {
		if (status === 'COMPLETED') {
			setBannerVisible(true);
			const t = setTimeout(() => setBannerVisible(false), 5000);
			return () => clearTimeout(t);
		}
		setBannerVisible(true);
	}, [status]);

	useEffect(() => {
		if (!locationId) return;

		setInitialising(true);
		setTargetEpochSec(null);
		localStorage.removeItem('nwl:nextSimulationEpoch');

		setDigitalTwinId(null);
		setLivePayload(null);
		lastRecommendedSimIdRef.current = null;
		userPickedStrategyRef.current = false;
	}, [locationId]);

	// KPI source
	const kpiBucket = useMemo(() => {
		const arr = Array.isArray(activePayload?.data) ? activePayload.data : [];
		const firstBucket = arr[0];
		if (firstBucket?.summary?.status === 'COMPLETED') return firstBucket;
		return arr.slice(1).find((b) => b?.summary?.status === 'COMPLETED') || null;
	}, [activePayload]);

	const selectedValue = options?.[selectedIndex]?.value;
	const kpisObj = useMemo(
		() => kpiExtraction(kpiBucket, selectedValue, kpisPayload),
		[kpiBucket, selectedValue, kpisPayload],
	);

	const metricPairs = useMemo(() => toDisplayPairs(kpisObj), [kpisObj]);
	const dataLength = metricPairs.length > 4 ? 4 : metricPairs.length;
	const hasMetrics = dataLength > 0;
	const strategiesReady = (options?.length ?? 0) > 0;
	const hasCompletedBucket = Boolean(kpiBucket);
	const showNoDataCard =
		!initialising && !displayFailedCard && !hasCompletedBucket && !strategiesReady && !hasMetrics;

	const recommendedStrategy = kpiBucket?.summary?.recommendedStrategy || null;
	const recKind =
		kpiBucket && recommendedStrategy
			? selectedValue === recommendedStrategy
				? 'recommended'
				: 'notRecommended'
			: 'noData';

	const recHeader = recKind === 'noData' ? 'No recommendation' : 'Recommendation';
	const selectedLabel =
		options?.[selectedIndex]?.displayText || options?.[selectedIndex]?.value || 'this strategy';
	const recText =
		recKind === 'noData'
			? 'No data available'
			: recKind === 'recommended'
				? `Use ${selectedLabel.toLowerCase()}`
				: `Do not use ${selectedLabel.toLowerCase()}`;
	const recBgClass =
		recKind === 'recommended'
			? 'kpi-use'
			: recKind === 'notRecommended'
				? 'kpi-do-not-use'
				: 'kpi-no-data';

	if (!decisionEngine) return null;

	const handleLearnMoreLink = (e) => {
		e.preventDefault();
		setShowCountdownInfo(true);
	};

	const hasNoStrategyRecommendation = useMemo(() => {
		const buckets = Array.isArray(activePayload?.data) ? activePayload.data : [];
		if (!buckets.length) return true;

		const completed = buckets.find((b) => b?.summary?.status === 'COMPLETED');

		if (!completed) return true;
		if (!completed?.summary?.recommendedStrategy) return true;

		return false;
	}, [activePayload]);

	const Placeholder = () => (
		<div className="kpi-empty kpi-empty--neutral" data-testid="kpi-loading-data">
			<div className="kpi-empty-title">Loading Data</div>
			<div className="kpi-empty-sub">
				<svg
					width="50"
					height="50"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M19.91 15.5752H15.38C15.1148 15.5752 14.8604 15.6806 14.6729 15.8681C14.4854 16.0557 14.38 16.31 14.38 16.5752C14.38 16.8405 14.4854 17.0948 14.6729 17.2824C14.8604 17.4699 15.1148 17.5752 15.38 17.5752H17.78C16.6769 18.728 15.2544 19.5246 13.6952 19.8626C12.1359 20.2007 10.5112 20.0648 9.02978 19.4725C7.54834 18.8801 6.27787 17.8583 5.38159 16.5384C4.48531 15.2185 4.00418 13.6607 4 12.0652C4 11.8 3.89464 11.5457 3.70711 11.3581C3.51957 11.1706 3.26522 11.0652 3 11.0652C2.73478 11.0652 2.48043 11.1706 2.29289 11.3581C2.10536 11.5457 2 11.8 2 12.0652C2.00529 14.018 2.58222 15.9266 3.6596 17.5553C4.73699 19.184 6.26767 20.4616 8.06274 21.2305C9.85782 21.9993 11.8387 22.2258 13.761 21.8819C15.6833 21.538 17.4628 20.6388 18.88 19.2952V21.0652C18.88 21.3305 18.9854 21.5848 19.1729 21.7724C19.3604 21.9599 19.6148 22.0652 19.88 22.0652C20.1452 22.0652 20.3996 21.9599 20.5871 21.7724C20.7746 21.5848 20.88 21.3305 20.88 21.0652V16.5652C20.8775 16.3069 20.7752 16.0595 20.5943 15.8749C20.4135 15.6904 20.1683 15.583 19.91 15.5752ZM12 2.06525C9.43639 2.07256 6.97349 3.06416 5.12 4.83525V3.06525C5.12 2.80003 5.01464 2.54568 4.82711 2.35814C4.63957 2.1706 4.38522 2.06525 4.12 2.06525C3.85478 2.06525 3.60043 2.1706 3.41289 2.35814C3.22536 2.54568 3.12 2.80003 3.12 3.06525V7.56525C3.12 7.83046 3.22536 8.08482 3.41289 8.27235C3.60043 8.45989 3.85478 8.56525 4.12 8.56525H8.62C8.88522 8.56525 9.13957 8.45989 9.32711 8.27235C9.51464 8.08482 9.62 7.83046 9.62 7.56525C9.62 7.30003 9.51464 7.04568 9.32711 6.85814C9.13957 6.6706 8.88522 6.56525 8.62 6.56525H6.22C7.32247 5.41312 8.74409 4.61675 10.3024 4.27835C11.8607 3.93996 13.4846 4.07499 14.9656 4.66611C16.4466 5.25723 17.7172 6.27746 18.6142 7.59585C19.5113 8.91424 19.9938 10.4706 20 12.0652C20 12.3305 20.1054 12.5848 20.2929 12.7724C20.4804 12.9599 20.7348 13.0652 21 13.0652C21.2652 13.0652 21.5196 12.9599 21.7071 12.7724C21.8946 12.5848 22 12.3305 22 12.0652C22 10.752 21.7413 9.45167 21.2388 8.23841C20.7362 7.02516 19.9997 5.92276 19.0711 4.99418C18.1425 4.06559 17.0401 3.329 15.8268 2.82645C14.6136 2.3239 13.3132 2.06525 12 2.06525Z"
						fill="#2A2A26"
					/>
				</svg>
			</div>
		</div>
	);

	const NoData = () => (
		<div className="kpi-empty-state">
			<div>
				<svg
					width="20"
					height="20"
					viewBox="0 0 12 12"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clip-path="url(#clip0_262_364796)">
						<path
							d="M10.6254 4.52032C10.0084 4.52032 9.48911 4.92199 9.31805 5.47354H8.62771V3.687C9.16532 3.50714 9.55631 3.00955 9.55631 2.41603C9.55631 1.67264 8.93928 1.06713 8.18174 1.06713C7.4242 1.06713 6.80717 1.67264 6.80717 2.41603C6.80717 2.99756 7.18594 3.48916 7.71133 3.681V5.47354H6.48338C6.44672 3.12346 6.35508 0.881282 6.2329 0.599512C6.19624 0.509585 6.14737 0.467619 6.12904 0.461624C5.95188 0.329731 5.6342 0.125897 5.34706 0.0539561C5.05382 -0.0179854 4.75447 -0.0179854 4.46123 0.0539561C4.16798 0.125897 3.90529 0.26978 3.67925 0.467619C3.45321 0.665458 3.28826 0.911258 3.1844 1.19303H3.07443C2.59181 1.19303 2.1214 1.38487 1.77928 1.7206C1.43717 2.05632 1.24167 2.51795 1.24167 2.99756C1.22945 3.09948 1.22945 3.20739 1.24167 3.3093C0.893445 3.50115 0.600202 3.77692 0.38638 4.10666C0.172557 4.43639 0.0442638 4.81408 0.00760847 5.20376C-0.0229376 5.59344 0.0381546 5.98912 0.196994 6.34883C0.355834 6.70854 0.594093 7.02628 0.905663 7.27208C0.704059 7.63778 0.606312 8.05144 0.61853 8.46511C0.630749 8.87877 0.746824 9.28644 0.966755 9.64614C1.18669 10.0058 1.49215 10.2996 1.86481 10.5034C2.23136 10.7073 2.6529 10.8152 3.07443 10.8092H3.1844C3.28826 11.085 3.45321 11.3368 3.67925 11.5346C3.89918 11.7324 4.16798 11.8763 4.46123 11.9483C4.75447 12.0202 5.05382 12.0142 5.34706 11.9483C5.34706 11.9483 6.08017 11.8104 6.34897 11.1389C6.42839 10.9471 6.47116 10.0238 6.48949 8.80083H7.33256C7.52194 9.32241 8.02901 9.7001 8.6216 9.7001C9.37915 9.7001 9.99618 9.09459 9.99618 8.3512C9.99618 7.6078 9.37915 7.0023 8.6216 7.0023C8.0229 7.0023 7.52194 7.37999 7.33256 7.90156H6.4956C6.4956 7.41596 6.4956 6.90038 6.4956 6.3788H9.35471C9.55631 6.8704 10.0512 7.22412 10.6254 7.22412C11.383 7.22412 12 6.61861 12 5.87521C12 5.13182 11.383 4.52631 10.6254 4.52631V4.52032ZM8.6216 7.89557C8.87208 7.89557 9.07979 8.0994 9.07979 8.3452C9.07979 8.591 8.87208 8.79484 8.6216 8.79484C8.37113 8.79484 8.16341 8.591 8.16341 8.3452C8.16341 8.0994 8.37113 7.89557 8.6216 7.89557ZM5.51812 3.9268C5.38372 3.85486 4.84611 3.5551 4.69949 3.50115C4.54676 3.44719 4.3757 3.45918 4.23519 3.53112C4.08856 3.60307 3.9786 3.72896 3.92973 3.87884C3.88085 4.02872 3.88696 4.19658 3.96027 4.34046C4.03358 4.48435 4.16188 4.59226 4.31461 4.64622C4.54676 4.73015 5.15768 5.11383 5.29819 5.31767C5.44481 5.5215 5.51812 5.75531 5.52423 6.00111V6.93036C5.38983 6.85841 4.9072 6.61261 4.76669 6.55866C4.61396 6.5107 4.4429 6.51669 4.30239 6.58863C4.15577 6.66057 4.0458 6.78647 3.99693 6.93635C3.94805 7.08623 3.95416 7.25409 4.02747 7.39797C4.10078 7.54186 4.22908 7.64977 4.38181 7.70373C4.61396 7.78766 5.16379 8.12338 5.3043 8.32122C5.45092 8.52506 5.52423 8.75887 5.53034 9.00467V10.2037C5.53034 10.3656 5.46314 10.5154 5.35317 10.6293C5.2371 10.7432 5.08437 10.8032 4.91942 10.8032C4.80334 10.8032 4.68727 10.7672 4.58952 10.7013C4.49177 10.6353 4.41235 10.5454 4.36348 10.4375C4.46734 10.3775 4.56508 10.3116 4.65672 10.2397C4.71781 10.1917 4.76669 10.1258 4.80945 10.0598C4.84611 9.98787 4.87054 9.91592 4.87665 9.83199C4.88276 9.75406 4.87665 9.67612 4.85222 9.59818C4.82778 9.52025 4.79112 9.4543 4.73614 9.39435C4.68116 9.3344 4.62007 9.28644 4.55287 9.24447C4.47955 9.2085 4.40624 9.18452 4.32682 9.17852C4.2474 9.17253 4.16798 9.17852 4.08856 9.2025C4.00914 9.22649 3.94194 9.26246 3.88085 9.31641C3.66092 9.49627 3.3799 9.59818 3.09276 9.59818C2.76897 9.59818 2.4574 9.47228 2.23136 9.24447C2.00532 9.01666 1.87703 8.71091 1.87703 8.39316C1.87703 8.16535 1.95034 7.94353 2.07252 7.75768C2.20693 7.78166 2.34744 7.79365 2.48795 7.79365C2.64679 7.79365 2.80563 7.72771 2.9217 7.61979C3.03778 7.50589 3.09887 7.35601 3.09887 7.19414C3.09887 7.03227 3.03167 6.88239 2.9217 6.76849C2.80563 6.65458 2.6529 6.59463 2.48795 6.59463C2.34133 6.59463 2.20082 6.57065 2.06641 6.51669C1.84648 6.43876 1.6571 6.30087 1.51048 6.12101C1.36996 5.94116 1.28443 5.71934 1.26 5.49153C1.23556 5.26371 1.28443 5.0359 1.3944 4.83206C1.50437 4.62823 1.66932 4.46037 1.87092 4.34646C1.95645 4.4244 2.0542 4.49634 2.15194 4.55629C2.29246 4.63423 2.4574 4.65821 2.61624 4.61624C2.77508 4.57427 2.90338 4.47236 2.98891 4.33447C3.07443 4.19658 3.09276 4.02872 3.05 3.87884C3.00723 3.72297 2.90338 3.59107 2.76287 3.51314C2.67734 3.46518 2.60403 3.39923 2.55515 3.3153C2.50017 3.21938 2.47573 3.10547 2.48184 2.99756C2.48184 2.83569 2.54904 2.68581 2.65901 2.57191C2.77508 2.458 2.92781 2.39805 3.09276 2.39805C3.13553 2.39805 3.17829 2.39805 3.22106 2.39805C3.2516 2.49397 3.29437 2.58989 3.33713 2.67982C3.3799 2.74576 3.42877 2.81171 3.49597 2.85967C3.55706 2.90763 3.63037 2.9436 3.70979 2.96159C3.78921 2.97957 3.86863 2.98557 3.94805 2.97957C4.02747 2.97358 4.10689 2.9436 4.17409 2.90164C4.3085 2.8237 4.40624 2.69181 4.44901 2.54193C4.49177 2.39205 4.46734 2.23018 4.39403 2.09829C4.34515 2.00836 4.31461 1.90045 4.31461 1.79854C4.31461 1.63667 4.38181 1.48679 4.49177 1.37288C4.60785 1.25897 4.76058 1.19902 4.92553 1.19902C5.09048 1.19902 5.24321 1.26497 5.35928 1.37288C5.47536 1.48679 5.53645 1.63667 5.53645 1.79854V3.9328L5.51812 3.9268ZM8.18174 2.87166C7.93126 2.87166 7.72355 2.66783 7.72355 2.42203C7.72355 2.17623 7.93126 1.97239 8.18174 1.97239C8.43222 1.97239 8.63993 2.17623 8.63993 2.42203C8.63993 2.66783 8.43222 2.87166 8.18174 2.87166ZM10.6254 6.31885C10.3749 6.31885 10.1672 6.11502 10.1672 5.86922C10.1672 5.62342 10.3749 5.41959 10.6254 5.41959C10.8759 5.41959 11.0836 5.62342 11.0836 5.86922C11.0836 6.11502 10.8759 6.31885 10.6254 6.31885Z"
							fill="#2A2A26"
						/>
					</g>
					<defs>
						<clipPath id="clip0_262_364796">
							<rect width="12" height="12" fill="white" />
						</clipPath>
					</defs>
				</svg>
			</div>

			<div className="kpi-empty-title">No strategy recommendation</div>

			<div className="kpi-empty-description">
				There is currently no digital twin configured for this location.
			</div>
		</div>
	);

	return (
		<div className="kpi-outer-wrapper">
			{!initialising && bannerVisible && !hasNoStrategyRecommendation && (
				<div className="items-center justify-center" role="status" id="kpi-status-banner">
					<div
						className={[`kpi-banner-bg ${status} rounded`, !showNoDataCard ? 'py-2' : ''].join(' ')}
					>
						<span className="kpi-banner-icon" aria-hidden="true">
							{status === 'IN_PROGRESS' && (
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M1.5 15.25V11.5H6.5V10.25H1.5V6.5H12.75V1.5C12.75 1.16848 12.6183 0.850537 12.3839 0.616116C12.1495 0.381696 11.8315 0.25 11.5 0.25H1.5C1.16848 0.25 0.850537 0.381696 0.616116 0.616116C0.381696 0.850537 0.25 1.16848 0.25 1.5V15.25C0.25 15.5815 0.381696 15.8995 0.616116 16.1339C0.850537 16.3683 1.16848 16.5 1.5 16.5H6.5V15.25H1.5ZM1.5 1.5H11.5V5.25H1.5V1.5ZM3.375 4C3.72018 4 4 3.72018 4 3.375C4 3.02982 3.72018 2.75 3.375 2.75C3.02982 2.75 2.75 3.02982 2.75 3.375C2.75 3.72018 3.02982 4 3.375 4ZM3.375 9C3.72018 9 4 8.72018 4 8.375C4 8.02982 3.72018 7.75 3.375 7.75C3.02982 7.75 2.75 8.02982 2.75 8.375C2.75 8.72018 3.02982 9 3.375 9ZM4 13.375C4 13.7202 3.72018 14 3.375 14C3.02982 14 2.75 13.7202 2.75 13.375C2.75 13.0298 3.02982 12.75 3.375 12.75C3.72018 12.75 4 13.0298 4 13.375ZM16.5001 11.1332V9.62503H17.7501V13.375H14.0001V12.125H15.6102C15.3848 11.6085 15.0239 11.1626 14.5657 10.8344C14.1075 10.5063 13.5691 10.3081 13.0075 10.261C12.4459 10.2138 11.882 10.3195 11.3755 10.5666C10.869 10.8138 10.4388 11.1934 10.1304 11.6651C9.82205 12.1368 9.64698 12.6831 9.62375 13.2462C9.60051 13.8093 9.72999 14.3682 9.99847 14.8637C10.2669 15.3592 10.6644 15.7729 11.1488 16.061C11.6332 16.3491 12.1865 16.5008 12.7501 16.5V17.75C11.9853 17.7505 11.2338 17.5503 10.5705 17.1696C9.90724 16.7888 9.35546 16.2407 8.97022 15.58C8.58499 14.9193 8.37978 14.1692 8.37508 13.4044C8.37038 12.6396 8.56636 11.887 8.94344 11.2216C9.32053 10.5563 9.86554 10.0014 10.5241 9.61255C11.1826 9.22365 11.9316 9.01428 12.6963 9.00533C13.4611 8.99639 14.2148 9.18818 14.8822 9.56156C15.5497 9.93495 16.1075 10.4769 16.5001 11.1332Z"
										fill="currentColor"
									/>
								</svg>
							)}
							{status === 'COMPLETED' && (
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
									<circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
									<path
										d="M6 10.5l2.5 2.5L14 8.5"
										stroke="currentColor"
										strokeWidth="1.6"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							)}
							{status === 'FAILED' && (
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
									<path
										d="M12.5 1.75C12.8315 1.75 13.1494 1.88179 13.3838 2.11621C13.6182 2.35063 13.75 2.66848 13.75 3V8H2.5V11.75H7.5V13H2.5V16.75H7.5V18H2.5C2.16848 18 1.85063 17.8682 1.61621 17.6338C1.38179 17.3994 1.25 17.0815 1.25 16.75V3C1.25 2.66848 1.38179 2.35063 1.61621 2.11621C1.85063 1.88179 2.16848 1.75 2.5 1.75H12.5ZM4.375 14.25C4.72018 14.25 5 14.5298 5 14.875C5 15.2202 4.72018 15.5 4.375 15.5C4.02982 15.5 3.75 15.2202 3.75 14.875C3.75 14.5298 4.02982 14.25 4.375 14.25ZM4.375 9.25C4.72018 9.25 5 9.52982 5 9.875C5 10.2202 4.72018 10.5 4.375 10.5C4.02982 10.5 3.75 10.2202 3.75 9.875C3.75 9.52982 4.02982 9.25 4.375 9.25ZM2.5 3V6.75H12.5V3H2.5ZM4.375 4.25C4.72018 4.25 5 4.52982 5 4.875C5 5.22018 4.72018 5.5 4.375 5.5C4.02982 5.5 3.75 5.22018 3.75 4.875C3.75 4.52982 4.02982 4.25 4.375 4.25Z"
										fill="currentColor"
									/>
									<path
										d="M15.1449 14.6117L18.1281 11.635C18.2588 11.5043 18.3321 11.3271 18.3321 11.1423C18.3321 10.9575 18.2588 10.7803 18.1281 10.6497C17.9975 10.519 17.8203 10.4456 17.6355 10.4456C17.4508 10.4456 17.2736 10.519 17.143 10.6497L14.1667 13.6333L11.1904 10.6497C11.0597 10.519 10.8825 10.4456 10.6978 10.4456C10.513 10.4456 10.3359 10.519 10.2052 10.6497C10.0746 10.7803 10.0012 10.9575 10.0012 11.1423C10.0012 11.3271 10.0746 11.5043 10.2052 11.635L13.1884 14.6117L10.2052 17.5884C10.1402 17.6529 10.0886 17.7297 10.0534 17.8142C10.0181 17.8988 10 17.9895 10 18.0811C10 18.1727 10.0181 18.2633 10.0534 18.3479C10.0886 18.4325 10.1402 18.5092 10.2052 18.5737C10.2697 18.6387 10.3464 18.6904 10.431 18.7256C10.5155 18.7608 10.6062 18.779 10.6978 18.779C10.7894 18.779 10.8801 18.7608 10.9646 18.7256C11.0491 18.6904 11.1259 18.6387 11.1904 18.5737L14.1667 15.5901L17.143 18.5737Z"
										fill="currentColor"
									/>
								</svg>
							)}
						</span>
						{!showNoDataCard && !hasNoStrategyRecommendation && (
							<p className="text-[14px] font-regular">{mapStatusText(status)}</p>
						)}
					</div>
				</div>
			)}

			<div className="kpi-header">
				<div className="kpi-header-row">
					<div className="kpi-title">Strategy prediction</div>
					<div className="kpi-info">
						{!hasNoStrategyRecommendation && (
							<a
								type="button"
								onMouseEnter={() => {
									setShowFloodInfo(true);
									setFloodIconActive(true);
								}}
								onMouseLeave={() => {
									setShowFloodInfo(false);
									setFloodIconActive(false);
								}}
								aria-label="Show KPI info"
								className="ms-2"
							>
								<svg
									width="17"
									height="17"
									viewBox="0 0 17 17"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M14.7681 8.5C14.7681 4.88505 11.8376 1.95455 8.22266 1.95455C4.6077 1.95455 1.6772 4.88505 1.6772 8.5C1.6772 12.115 4.6077 15.0455 8.22266 15.0455C11.8376 15.0455 14.7681 12.115 14.7681 8.5ZM7.49538 11.4091V8.5C7.49538 8.09834 7.82099 7.77273 8.22266 7.77273C8.62432 7.77273 8.94993 8.09834 8.94993 8.5V11.4091C8.94993 11.8108 8.62432 12.1364 8.22266 12.1364C7.82099 12.1364 7.49538 11.8108 7.49538 11.4091ZM8.22976 4.86364C8.63142 4.86364 8.95703 5.18925 8.95703 5.59091C8.95703 5.99257 8.63142 6.31818 8.22976 6.31818H8.22266C7.82099 6.31818 7.49538 5.99257 7.49538 5.59091C7.49538 5.18925 7.82099 4.86364 8.22266 4.86364H8.22976ZM16.2227 8.5C16.2227 12.9183 12.6409 16.5 8.22266 16.5C3.80438 16.5 0.222656 12.9183 0.222656 8.5C0.222656 4.08172 3.80438 0.5 8.22266 0.5C12.6409 0.5 16.2227 4.08172 16.2227 8.5Z"
										fill={floodIconActive ? '#077898' : '#535662'}
									/>
								</svg>
							</a>
						)}

						{showFloodInfo && !hasNoStrategyRecommendation && (
							<div className="kpi-tooltip" role="tooltip">
								<div aria-hidden className="kpi-tooltip-arrow" />
								The information displayed is based on available forecast data. Impacted assets
								without forecast data won’t be included in this KPI panel, or on the map.
							</div>
						)}
					</div>
				</div>
				{!hasNoStrategyRecommendation && (
					<div className="kpi-subtitle">Select a strategy to view 12-hour KPI’s</div>
				)}
				{!hasNoStrategyRecommendation && (
					<div className="kpi-dropdown-wrap">
						<Dropdown
							options={options}
							onChange={handleSelect}
							selectedIndex={Math.min(
								Math.max(selectedIndex, 0),
								Math.max(0, (options?.length ?? 1) - 1),
							)}
							emptySelectionText="Choose strategy"
							recommendedValue={recommendedStrategy}
							implementedValue={implementedStrategy}
							showActiveBadge={true}
							showMainBadge={true}
						/>
					</div>
				)}
			</div>

			{initialising && !activePayload ? (
				<Placeholder />
			) : displayFailedCard ? (
				<div className="kpi-empty-state">
					<svg width="24" height="25" viewBox="0 0 24 25" fill="none">
						<path
							d="M15 1.56525C15.3978 1.56525 15.7792 1.7234 16.0605 2.0047C16.3419 2.286 16.5 2.66742 16.5 3.06525V9.06525H3V13.5652H9V15.0652H3V19.5652H9V21.0652H3C2.60218 21.0652 2.22076 20.9071 1.93945 20.6258C1.65815 20.3445 1.5 19.9631 1.5 19.5652V3.06525C1.5 2.66742 1.65815 2.286 1.93945 2.0047C2.22076 1.7234 2.60218 1.56525 3 1.56525H15ZM5.25 16.5652C5.66421 16.5652 6 16.901 6 17.3152C6 17.7295 5.66421 18.0652 5.25 18.0652C4.83579 18.0652 4.5 17.7295 4.5 17.3152C4.5 16.901 4.83579 16.5652 5.25 16.5652ZM5.25 10.5652C5.66421 10.5652 6 10.901 6 11.3152C6 11.7295 5.66421 12.0652 5.25 12.0652C4.83579 12.0652 4.5 11.7295 4.5 11.3152C4.5 10.901 4.83579 10.5652 5.25 10.5652ZM3 3.06525V7.56525H15V3.06525H3ZM5.25 4.56525C5.66421 4.56525 6 4.90103 6 5.31525C6 5.72946 5.66421 6.06525 5.25 6.06525C4.83579 6.06525 4.5 5.72946 4.5 5.31525C4.5 4.90103 4.83579 4.56525 5.25 4.56525Z"
							fill="black"
						/>
						<path
							d="M18.1739 16.9993L21.7537 13.4272C21.9105 13.2704 21.9986 13.0578 21.9986 12.8361C21.9986 12.6143 21.9105 12.4017 21.7537 12.2449C21.597 12.0881 21.3843 12 21.1626 12C20.9409 12 20.7283 12.0881 20.5715 12.2449L17 15.8253L13.4285 12.2449C13.2717 12.0881 13.0591 12 12.8374 12C12.6157 12 12.403 12.0881 12.2463 12.2449C12.0895 12.4017 12.0014 12.6143 12.0014 12.8361C12.0014 13.0578 12.0895 13.2704 12.2463 13.4272L15.8261 16.9993L12.2463 20.5713C12.1682 20.6488 12.1063 20.7408 12.064 20.8423C12.0218 20.9438 12 21.0526 12 21.1625C12 21.2724 12.0218 21.3813 12.064 21.4827C12.1063 21.5842 12.1682 21.6763 12.2463 21.7537C12.3237 21.8317 12.4157 21.8937 12.5172 21.936C12.6186 21.9782 12.7275 22 12.8374 22C12.9473 22 13.0561 21.9782 13.1575 21.936C13.259 21.8937 13.3511 21.8317 13.4285 21.7537L17 18.1733L20.5715 21.7537C20.6489 21.8317 20.741 21.8937 20.8425 21.936C20.9439 21.9782 21.0527 22 21.1626 22C21.2725 22 21.3814 21.9782 21.4828 21.936C21.5843 21.8937 21.6763 21.8317 21.7537 21.7537C21.8318 21.6763 21.8937 21.5842 21.936 21.4827C21.9782 21.3813 22 21.2724 22 21.1625C22 21.0526 21.9782 20.9438 21.936 20.8423C21.8937 20.7408 21.8318 20.6488 21.7537 20.5713L18.1739 16.9993Z"
							fill="black"
						/>
					</svg>
					<div className="kpi-empty-title">Data update failed</div>
					<div className="kpi-empty-description">
						{remainingRefreshTime ? (
							<>We’ll try again in {remainingRefreshTime}</>
						) : (
							'We’ll try again soon.'
						)}
					</div>
					<a className="kpi-empty-link" onClick={handleLearnMoreLink}>
						Learn more about data updates
					</a>
				</div>
			) : !showNoDataCard && !hasNoStrategyRecommendation ? (
				<div className="kpi-metrics-row">
					{metricPairs.map(({ value, description, testId }, idx) => {
						const isFlood = testId === 'flood-locations';
						const isDischarge = testId === 'discharge-locations';
						const clickable = isFlood || isDischarge;
						return (
							<div
								key={`metric-${idx}`}
								className={`kpi-metric-col ${idx < 2 ? 'kpi-metric-col--divider' : ''}`}
								data-testid={`kpi-${testId}-container`}
								onClick={() => {
									if (!clickable) return;
									if (isFlood) setOpenLocations('flood');
									if (isDischarge) setOpenLocations('discharge');
								}}
								role={clickable ? 'button' : undefined}
								tabIndex={clickable ? 0 : undefined}
								onKeyDown={(e) => {
									if (!clickable) return;
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										if (isFlood) setOpenLocations('flood');
										if (isDischarge) setOpenLocations('discharge');
									}
								}}
								style={clickable ? { cursor: 'pointer' } : undefined}
							>
								<p
									className="kpi-metric-value"
									data-testid={`kpi-${testId}-value`}
									id={`kpi-${testId}-value`}
								>
									{value}
								</p>
								<p
									data-testid={`kpi-${testId}-desc`}
									id={`kpi-${testId}-desc`}
									className="kpi-metric-label"
								>
									{description}
								</p>
							</div>
						);
					})}
				</div>
			) : (
				<NoData />
			)}

			{showCountdownInfo && (
				<CountdownInfoModal
					open={showCountdownInfo}
					onClose={() => setShowCountdownInfo(false)}
					lastSuccessAt={lastSuccessfulRefreshTime}
				/>
			)}

			{!hasNoStrategyRecommendation && (
				<div className={`kpi-recommendation-bar ${recBgClass}`}>
					<div className={`kpi-rec-icon ${recKind}`}>
						{recKind === 'notRecommended' && <WarningOutlined />}
						{recKind === 'recommended' && <ToolOutlined />}
						{recKind === 'noData' && <NoDataCloud />}
					</div>
					<div className="kpi-rec-text">
						<div className="kpi-rec-title">{recHeader}</div>
						<div className="kpi-rec-main">{recText}</div>
					</div>
				</div>
			)}

			<LocationsModal
				open={openLocations === 'flood'}
				title="Flood locations"
				items={floodLocations}
				onClose={() => setOpenLocations(null)}
			/>

			<LocationsModal
				open={openLocations === 'discharge'}
				title="Discharge locations"
				items={dischargeLocations}
				onClose={() => setOpenLocations(null)}
			/>
		</div>
	);
};

export default Kpi;
